import {
  Box,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  InputLabelProps,
  MenuItem,
  Select,
  SelectProps,
} from '@material-ui/core';
import { ReactNode, useMemo } from 'react';
import { FieldValues } from 'react-hook-form';

import { Font } from '@/components/shared/components/Font';
import { useCustomFieldStyles } from '@/components/shared/components/NewContextForms/hooks/useCustomFieldStyles';
import { CustomFormLabel } from '@/components/shared/components/NewContextForms/shared/CustomFormLabel';
import { CustomFormLabelWithAdornment } from '@/components/shared/components/NewContextForms/shared/CustomFormLabelWithAdornment';
import { CustomTextFieldExtraProps } from '@/components/shared/components/NewContextForms/shared/CustomTextField';
import { SelectGroupHeader } from '@/components/shared/components/NewContextForms/shared/SelectGroupHeader';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { SelectOption } from '@/types/selectOption';

export interface CustomSelectFieldExtraProps {
  selectOptions: SelectOption[];
}

export interface CustomSelectFieldProps<TFieldValues extends FieldValues>
  extends Omit<SelectProps, 'name' | 'error' | 'style' | 'label'>,
    CustomTextFieldExtraProps<TFieldValues>,
    CustomSelectFieldExtraProps {
  InputLabelProps?: InputLabelProps;
}

export function CustomSelectField<TFieldValues extends FieldValues>({
  field,
  fieldVariant = FieldVariant.dataRow,
  error,
  tooltipTitle,
  fullWidth = true,
  valueUnit,
  labelIcon,
  label,
  strongTitle,
  labelDynamicDataTexts,
  labelIconPosition,
  labelSuffix,
  highlightLabel,
  selectOptions,
  placeholder = 'SELECT',
  InputLabelProps,
  ...props
}: CustomSelectFieldProps<TFieldValues>) {
  const { getTextIdTranslation } = useTranslation();
  const { getMUISelectClasses, getMUIMenuItemClasses } = useCustomFieldStyles({
    disabled: props.disabled,
    field: !!field,
    fieldVariant: fieldVariant,
    highlightLabel,
  });

  const fieldVariantIsForm = fieldVariant === FieldVariant.form;

  const selectClasses = getMUISelectClasses();
  const menuItemClasses = getMUIMenuItemClasses();

  // The list padding would leave a gap above the first sticky group header
  const hasGroups = selectOptions?.some((option) => option.group);

  const currentValue = useMemo(
    () => (field?.value === false ? false : props.value === false ? false : field?.value || props.value || ''),
    [field?.value, props.value],
  );

  return (
    <>
      <FormControl fullWidth classes={selectClasses}>
        {fieldVariantIsForm && label && (
          <InputLabel shrink={fieldVariant === FieldVariant.form} {...InputLabelProps}>
            <Box component="span" display="inline-flex" alignItems="center">
              {typeof label === 'string' ? (
                <CustomFormLabel
                  label={label}
                  strongTitle={strongTitle}
                  labelDynamicDataTexts={labelDynamicDataTexts}
                  color={highlightLabel ? 'textPrimary' : 'textSecondary'}
                />
              ) : (
                (label as ReactNode)
              )}
              {labelSuffix}
            </Box>
          </InputLabel>
        )}
        <>
          <Select
            fullWidth={fullWidth}
            classes={selectClasses}
            endAdornment={
              valueUnit && props.value ? (
                <InputAdornment position="end" style={{ paddingLeft: '4px' }}>
                  <Font variant="subtitle2" color={'textSecondary'}>
                    <strong>{valueUnit}</strong>
                  </Font>
                </InputAdornment>
              ) : null
            }
            startAdornment={
              !fieldVariantIsForm &&
              label && (
                <CustomFormLabelWithAdornment
                  tooltipTitle={tooltipTitle}
                  labelIcon={labelIcon}
                  label={label}
                  labelIconPosition={labelIconPosition}
                  labelDynamicDataTexts={labelDynamicDataTexts}
                />
              )
            }
            renderValue={(e) => {
              if (e === false) {
                return <>{selectOptions.find((element) => element.value === false)?.display}</>;
              }

              if (fieldVariantIsForm && (e === 'undefined' || !e)) {
                return (
                  <div style={{ opacity: 0.4 }}>
                    <Text tId={placeholder} />
                  </div>
                );
              }
              if (field) {
                return <>{selectOptions.find((element) => element.value === e)?.display}</>;
              }
              if (typeof e === 'string' && getTextIdTranslation(e as string)) {
                return <>{getTextIdTranslation(e as string)}</>;
              }
              return e;
            }}
            {...(hasGroups && { MenuProps: { MenuListProps: { disablePadding: true } } })}
            {...props}
            defaultValue={!field ? props.defaultValue : null}
            {...field}
            value={currentValue}
            {...(error && {
              error: !!error,
            })}
            inputRef={field?.ref}
            displayEmpty={props.displayEmpty || fieldVariantIsForm}
          >
            {selectOptions?.length > 0 ? (
              // Flat array instead of Fragments: Select only accepts its options as direct children
              selectOptions?.flatMap((option, i) => {
                const menuItem = (
                  <MenuItem key={i} value={option.value} classes={menuItemClasses}>
                    {option.display}
                  </MenuItem>
                );
                const startsGroup = !!option.group && option.group !== selectOptions[i - 1]?.group;
                return startsGroup
                  ? [
                      <SelectGroupHeader key={`group-${option.group}`} title={option.group} divider={i > 0} disabled />,
                      menuItem,
                    ]
                  : [menuItem];
              })
            ) : (
              <MenuItem disabled value={undefined} classes={menuItemClasses}>
                <Text tId={'NO_OPTIONS_TO_DISPLAY'} />
              </MenuItem>
            )}
          </Select>
          {!!error && <FormHelperText>{error.message}</FormHelperText>}
        </>
      </FormControl>
    </>
  );
}
